import { Button } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  IdcardOutlined,
  TrophyOutlined,
} from '../icons';
import { formatDate, formatDateTime, labHeadline } from '../lib/format';
import { publicApi } from '../services/resources';
import { getUser, homePath } from '../stores/session';
import type { PublicOverview } from '../types/api';

const ROLE_LABEL: Record<string, string> = { admin: '实验员', teacher: '教师', student: '学生' };

const GUIDE = [
  {
    icon: <ExperimentOutlined />,
    title: '开放对象',
    text: '本校高中学生；教师可代学生提交实验申请。',
  },
  {
    icon: <IdcardOutlined />,
    title: '账号与审核',
    text: '使用 G＋身份证号码注册，学生、教师均需实验员审核。',
  },
  {
    icon: <CheckCircleOutlined />,
    title: '使用原则',
    text: '先预约后使用，按审核通过的实验室与时段到室。',
  },
];

const STEPS = [
  { no: '01', title: '注册账号', text: '使用 G＋身份证号码注册，提交后等待实验员审核。' },
  { no: '02', title: '提交申请', text: '登录后选择实验室、日期与节次，填写实验内容。' },
  { no: '03', title: '等待审核', text: '审核结果在系统内反馈；通过后按预约时段到室。' },
  { no: '04', title: '展示成果', text: '符合公开与授权要求的成果，可在首页成果区展示。' },
];

export function PublicHomePage() {
  const nav = useNavigate();
  const user = getUser();
  const [data, setData] = useState<PublicOverview | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    publicApi
      .overview()
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  const coursePath =
    user?.role === 'admin'
      ? '/admin/courses'
      : user?.role === 'student'
        ? '/student/courses'
        : user
          ? '/teacher/schedule'
          : '/login';

  return (
    <div className="pub">
      <header className="pub-header">
        <div className="pub-brand">
          <img
            className="pub-logo"
            src="/brand/logo-white.webp"
            alt="成都市第二十中学校"
            width={228}
            height={74}
          />
          <span className="pub-brand-divider" aria-hidden="true" />
          <span className="pub-brand-name">大同科创共享中心预约平台</span>
        </div>
        {user ? (
          <div className="pub-actions">
            <span className="pub-user">
              {ROLE_LABEL[user.role] ?? '用户'} {user.name}
            </span>
            <Button type="primary" className="touch" onClick={() => nav(homePath(user.role, false))}>
              进入工作台
            </Button>
          </div>
        ) : (
          <Button type="primary" className="touch" onClick={() => nav('/login')}>
            一键登录
          </Button>
        )}
      </header>

      <main className="pub-main">
        <section className="pub-intro">
          <p className="pub-eyebrow">成都市第二十中学校 · 高中教育技术保障中心</p>
          <h1>大同科创共享中心</h1>
          <p>{data?.center.description ?? '面向本校高中学生开放实验室预约、开放实验课程与成果分享。'}</p>
          <p className="pub-intro-line">{labHeadline(data ?? undefined)}</p>
          <div className="pub-intro-actions">
            <Button type="primary" className="touch" onClick={() => scrollTo('pub-courses')}>
              看看有什么课
            </Button>
            <Button className="touch" onClick={() => scrollTo('pub-labs')}>
              实验室一览
            </Button>
          </div>
        </section>

        <section className="pub-guide" aria-label="服务说明">
          {GUIDE.map((item) => (
            <div className="pub-guide-item" key={item.title}>
              <span className="pub-guide-icon">{item.icon}</span>
              <div>
                <h2>{item.title}</h2>
                <p>{item.text}</p>
              </div>
            </div>
          ))}
        </section>

        {failed ? (
          <div className="pub-alert" role="alert">
            内容加载失败，请稍后刷新。首页说明仍可查看。
          </div>
        ) : null}

        <section id="pub-courses" className="pub-section">
          <div className="pub-section-head">
            <h2>近期可报名</h2>
            <span className="meta">{data ? `共 ${data.courses_upcoming.length} 门在招` : '加载中'}</span>
          </div>
          {data && data.courses_upcoming.length === 0 ? (
            <div className="pub-empty">
              <ClockCircleOutlined />
              <p>近期暂无开放报名的课程</p>
              <span>课程发布后会在这里出现，可先登录查看实验室与预约入口。</span>
            </div>
          ) : (
            <div className="pub-cards">
              {(data?.courses_upcoming ?? []).map((course) => (
                <article key={course.id} className="pub-card">
                  <h3>{course.title}</h3>
                  <p className="pub-card-sub">{course.summary || '开放实验课程'}</p>
                  <p className="pub-card-meta">
                    <ClockCircleOutlined /> {course.room} · {course.lab_name}
                  </p>
                  <p className="pub-card-meta">开课 {formatDateTime(course.starts_at)}</p>
                  <p className="pub-card-meta">报名至 {formatDateTime(course.signup_deadline)}</p>
                  <div className="pub-card-foot">
                    <span className={course.remaining <= 3 ? 'pub-remaining low' : 'pub-remaining'}>
                      余 {course.remaining} / {course.capacity}
                    </span>
                    <Button size="small" onClick={() => nav(coursePath)}>
                      查看
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section id="pub-labs" className="pub-section">
          <div className="pub-section-head">
            <h2>实验室</h2>
            <span className="meta">{data ? `共 ${data.labs.total} 间对外开放预约` : '加载中'}</span>
          </div>
          {data && data.labs.items.length === 0 ? (
            <div className="pub-empty">
              <ExperimentOutlined />
              <p>实验室信息录入中</p>
              <span>主数据核验完成后自动展示房间号、名称与容量。</span>
            </div>
          ) : (
            <div className="pub-labs">
              {(data?.labs.items ?? []).map((lab) => (
                <article key={lab.id} className="pub-card">
                  <span className="mono pub-room">{lab.room_code}</span>
                  <h3>{lab.name}</h3>
                  <p className="pub-card-sub">
                    {[lab.campus, lab.capacity ? `${lab.capacity} 人` : null].filter(Boolean).join(' · ')}
                  </p>
                  <span className="pub-badge">可预约</span>
                </article>
              ))}
            </div>
          )}
        </section>

        <section id="pub-showcases" className="pub-section">
          <div className="pub-section-head">
            <h2>成果</h2>
            <span className="meta">
              <TrophyOutlined /> 公开展示
            </span>
          </div>
          {data && data.showcases.length === 0 ? (
            <div className="pub-empty">
              <TrophyOutlined />
              <p>成果整理中</p>
              <span>仅展示已发布、互联网公开且满足授权要求的成果。</span>
            </div>
          ) : (
            <div className="pub-cards">
              {(data?.showcases ?? []).map((item) => (
                <article key={item.id} className="pub-card pub-showcase">
                  <h3>{item.title}</h3>
                  <p className="pub-card-sub">{item.summary || '成果展示'}</p>
                  <p className="pub-card-meta">{formatDate(item.published_at)}</p>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="pub-section">
          <div className="pub-section-head">
            <h2>如何预约</h2>
            <span className="meta">四步完成</span>
          </div>
          <ol className="pub-steps">
            {STEPS.map((step) => (
              <li key={step.no}>
                <span className="mono pub-step-no">{step.no}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="pub-section">
          <div className="pub-section-head">
            <h2>开展情况</h2>
            <span className="meta">由系统实际记录自动更新</span>
          </div>
          <div className="pub-stats">
            <div>
              <strong>{data && data.activity_total > 0 ? data.activity_total : '—'}</strong>
              <span>已开展活动</span>
            </div>
            <div>
              <strong>{data && data.labs.total > 0 ? data.labs.total : '—'}</strong>
              <span>开放实验室</span>
            </div>
            <div>
              <strong>{data && data.courses_upcoming.length > 0 ? data.courses_upcoming.length : '—'}</strong>
              <span>
                <ExperimentOutlined /> 在招开放课
              </span>
            </div>
          </div>
          {data && data.activity_total === 0 && data.labs.total === 0 ? (
            <p className="meta">统计从首次实验室开放与活动完成后开始累计。</p>
          ) : null}
        </section>
      </main>

      <footer className="pub-footer">
        <p>大同科创共享中心预约平台</p>
        <p>成都市第二十中学校 · 高中教育技术保障中心</p>
        <p>厚德明理 · 励志笃行 · 情智并育 · 慧行天下</p>
        <p className="pub-footer-addr">地址：四川省成都市金牛区育英路 6 号</p>
        <p>
          <a href="/login">登录</a> · <a href="/register">注册账号</a>
        </p>
        <p className="pub-footer-license">
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer noopener">
            蜀ICP备15006431号-2
          </a>
        </p>
        <p className="pub-footer-copy">Copyright © 成都市第二十中学校</p>
      </footer>
    </div>
  );
}

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
